import { useState, useEffect, useMemo, createContext } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import {
  Home,
  GraduationCap,
  Bookmark,
  ShoppingCart,
  User as UserIcon,
  MessageSquare,
  FolderOpen,
  Trophy,
  LifeBuoy,
  Search,
  Calendar,
  Bell,
} from "lucide-react";
import DashboardLayout from "../dashboard/DashboardLayout";
import NavItem, { NavSection } from "../dashboard/components/navItem";
import StudentDashboardFooter from "./components/footer";
import CalendarDropdown from "./components/calendarDropdown";
import ScrollToTop from "../../utils/scrollToTop";
import { useEnrollmentsQuery } from "../../hooks/queries/useCourseQueries";
import RoleSwitcher from "../../components/layout/RoleSwitcher";

export const userInfo = createContext(null);
export const enrollmentDataContext = createContext(null);

const StudentDashboardLayout = () => {
  const location = useLocation();

  const { data: enrollmentData = [], isLoading: enrollmentDataLoading } = useEnrollmentsQuery();

  const {
    inProgressCourses,
    inProgressTracks,
    completedCourses,
    completedTracks,
  } = useMemo(() => {
    const dataList = Array.isArray(enrollmentData) ? enrollmentData : [];
    const allCompleteCourseTracks = dataList.filter((e) => e.is_completed);
    const allinCompleteCourseTracks = dataList.filter((e) => !e.is_completed);

    return {
      inProgressCourses: allinCompleteCourseTracks.filter((item) => item.course_detail),
      inProgressTracks: allinCompleteCourseTracks.filter((item) => item.track_detail),
      completedCourses: allCompleteCourseTracks.filter((item) => item.course_detail),
      completedTracks: allCompleteCourseTracks.filter((item) => item.track_detail),
    };
  }, [enrollmentData]);

  useEffect(() => {
    localStorage.setItem("from", location.pathname);
  }, [location.pathname]);

  const SidebarNav = (
    <>
      <NavItem to="/student/dashboard" icon={Home} label="Dashboard" end />

      <NavSection label="Academics & Learning" />
      <NavItem
        to="/student/dashboard/courses"
        icon={GraduationCap}
        label="Courses"
        end
      />
      <NavItem
        to="/student/dashboard/my-courses"
        icon={Bookmark}
        label="My Learning"
        end
      />
      <NavItem
        to="/student/dashboard/certification"
        icon={Trophy}
        label="Certification"
        end
      />
      <NavItem
        to="/student/dashboard/resources"
        icon={FolderOpen}
        label="Resources"
        end
      />

      <NavSection label="Community & Events" />
      <NavItem
        to="/student/dashboard/community"
        icon={MessageSquare}
        label="Community"
        end
      />
      <NavItem
        to="/student/dashboard/event"
        icon={Calendar}
        label="Events"
        end
      />

      <NavSection label="Finance & Support" />
      <NavItem
        to="/student/dashboard/payments"
        icon={ShoppingCart}
        label="Payments"
        end
      />
      <NavItem
        to="/student/dashboard/notifications"
        icon={Bell}
        label="Notifications"
        end
      />
      <NavItem
        to="/student/dashboard/support"
        icon={LifeBuoy}
        label="Support"
        end
      />

      <NavSection label="Account & Preferences" />
      <NavItem
        to="/student/dashboard/profile"
        icon={UserIcon}
        label="Profile & Settings"
        end
      />
    </>
  );

  const TopBarAddons = (
    <div className="cht-flex cht-items-center cht-gap-2">
      <RoleSwitcher />
      <div className="cht-hidden md:cht-flex cht-relative cht-mr-2 cht-group">
        <Search
          className="cht-absolute cht-left-3 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400 group-focus-within:cht-text-[#3e57da] cht-transition-colors"
          size={16}
        />
        <input
          type="text"
          placeholder="Search content..."
          className="cht-w-40 lg:cht-w-64 cht-bg-white/50 cht-border cht-border-gray-200/50 cht-rounded-xl cht-py-2 cht-pl-9 cht-pr-4 cht-text-xs cht-font-medium cht-focus:ring-2 cht-focus:ring-[#3e57da]/10 cht-focus:border-[#3e57da] cht-outline-none cht-transition-all"
        />
      </div>
      <Link
        to="/student/dashboard/payments"
        className="cht-p-2 cht-bg-gray-100 cht-rounded-[10px] cht-text-gray-500 hover:cht-bg-gray-100 hover:cht-text-[#3e57da] cht-transition-all cht-relative"
      >
        <ShoppingCart size={18} />
        <span className="cht-absolute cht-top-1 cht-right-1 cht-w-2 cht-h-2 cht-bg-red-500 cht-rounded-full"></span>
      </Link>
      <CalendarDropdown />
    </div>
  );

  return (
    <>
      <enrollmentDataContext.Provider
        value={{
          enrollmentData,
          enrollmentDataLoading,
          inProgressCourses,
          completedCourses,
          inProgressTracks,
          completedTracks,
        }}
      >
        <ScrollToTop />
        <DashboardLayout
          sidebarNavContent={SidebarNav}
          topBarAddons={TopBarAddons}
          footerComponent={<StudentDashboardFooter />}
        >
          <Outlet />
        </DashboardLayout>
      </enrollmentDataContext.Provider>
    </>
  );
};

export default StudentDashboardLayout;
